import TippyHeadless from '@tippyjs/react/headless';
import { MentionElementProps } from '@udecode/plate-ui-mention';
import { useIsMobile } from 'apps/exegesis/styles/themes';
import { useRouter } from 'next/router';
import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { useSelected } from 'slate-react';
import styled from 'styled-components';
import { useAuthor } from '../../../utils/useAuthor';
import { usePrivacy } from '../../../utils/usePrivacy';
import { RenderPreview } from './RenderPreview';

export const CreateMentionSlateComponent = (allowPreview: boolean) => {
  return function MentionSlateComponentRenderer({
    attributes,
    element,
    prefix,
    children,
  }: MentionElementProps) {
    const router = useRouter();
    const privacy = usePrivacy();
    const author = useAuthor((state) => state.author);
    const ScrollContainerRef = useRef(null as any);
    const BacklinksRef = useRef(null as any);

    const selected = useSelected();
    const isMobile = useIsMobile();

    const PreviewRenderer = useMemo(() => {
      return RenderPreview(
        element.value,
        BacklinksRef,
        ScrollContainerRef,
        author,
        privacy === 'public'
      );
    }, [author, privacy, element.value]);

    const mention = (
      <Mention
        className="tag-mention-node"
        selected={selected}
        data-slate-value={element.value}
        contentEditable={false}
        {...(attributes || {})}
        onClick={() => {
          if (privacy === 'private') {
            router.push('/notes/tag/' + encodeURIComponent(element.value));
          } else {
            router.push(
              '/user/' + author + '/tag/' + encodeURIComponent(element.value)
            );
          }
        }}
      >
        {prefix}
        {element.value}
        {children}
      </Mention>
    );
    // if it is mobile or we aren't allow a preview, then show only the mention
    return isMobile || !allowPreview ? (
      mention
    ) : (
      <LazyTippy
        render={PreviewRenderer}
        offset={[0, 0]}
        placement="right-start"
        arrow={false}
        interactive={true}
      >
        {mention}
      </LazyTippy>
    );
  };
};

const Mention = styled.span<{ selected: boolean }>`
  display: inline-block;
  color: ${(props) => props.theme.colors.link.text};
  line-height: 1;
  vertical-align: text-top;
  text-decoration: none !important;
  background-color: ${(props) => props.theme.colors.link.background};
  font-size: 1.05em;
  padding: 0.1em 0.1em 0.1em 0.1em;
  border-radius: 0.2em;
  /* border: ${(props) => `1px solid ${props.theme.colors.link.text}`}; */
  outline: ${(props) =>
    props.selected ? `2px solid ${props.theme.colors.link.text}` : 'none'};
  cursor: default;

  :hover {
    outline: ${(props) => `1px solid ${props.theme.colors.link.text}`};
  }
`;

export const LazyTippy = forwardRef(function LazyTippyComponent(
  props: any,
  ref: any
) {
  const [mounted, setMounted] = useState(false);
  const lazyPlugin = {
    fn: () => ({
      onMount: () => setMounted(true),
      onHidden: () => setMounted(false),
    }),
  };
  const computedProps = { ...props };

  computedProps.plugins = [lazyPlugin, ...(props.plugins || [])];

  if (props.render) {
    computedProps.render = (...args: any) =>
      mounted ? props.render(...args) : '';
  } else {
    computedProps.content = mounted ? props.content : '';
  }

  return <TippyHeadless {...computedProps} ref={ref} />;
});
